import * as SharedRequestContracts from "@repo/execution-engine-contracts/requests";
import {
  ExecutionLiveClientMessageSchema,
  ExecutionLiveEventSchema,
  ExecutionLiveServerMessageSchema,
  ExecutionLiveSnapshotSchema,
  ExecutionMarketOrderQuotePreviewSchema,
  ExecutionOndoDirectRebalancePlanPreviewSchema,
  ExecutionOperationEventAckSchema,
  ExecutionOperationResponseSchema,
  ExecutionRebalanceCreateResponseSchema,
  ExecutionSpecDrivenBuyCreatedFundingSchema,
  ExecutionSpecDrivenBuyFundingRecoveryResponseSchema,
  ExecutionSpecDrivenBuyOperationResponseSchema,
  ExecutionSpecDrivenBuyPlanBasisSchema,
  ExecutionSpecDrivenBuyPreviewResponseSchema,
  LiveDescriptorSchema,
  PortfolioExecutionFeedClientMessageSchema,
  PortfolioExecutionFeedOperationSchema,
  PortfolioExecutionFeedServerMessageSchema,
  PortfolioExecutionFeedSnapshotSchema,
  PortfolioFeedLiveDescriptorSchema,
  UserExecutionFeedClientMessageSchema,
  UserExecutionFeedOperationSchema,
  UserExecutionFeedServerMessageSchema,
  UserExecutionFeedSnapshotSchema,
  UserFeedLiveDescriptorSchema,
  OndoDirectRebalancePlanSchema,
} from "@repo/execution-engine-contracts/responses";
import type { ExecutionLiveJsonValue } from "@repo/execution-engine-contracts/responses";
import type { CapabilityScope } from "@repo/capability-token";
import { z } from "zod";

export {
  ExecutionLiveClientMessageSchema,
  ExecutionLiveEventSchema,
  ExecutionLiveServerMessageSchema,
  ExecutionLiveSnapshotSchema,
  ExecutionMarketOrderQuotePreviewSchema,
  ExecutionOndoDirectRebalancePlanPreviewSchema,
  ExecutionOperationEventAckSchema,
  ExecutionOperationResponseSchema,
  ExecutionRebalanceCreateResponseSchema,
  ExecutionSpecDrivenBuyCreatedFundingSchema,
  ExecutionSpecDrivenBuyFundingRecoveryResponseSchema,
  ExecutionSpecDrivenBuyOperationResponseSchema,
  ExecutionSpecDrivenBuyPlanBasisSchema,
  ExecutionSpecDrivenBuyPreviewResponseSchema,
  LiveDescriptorSchema,
  PortfolioExecutionFeedClientMessageSchema,
  PortfolioExecutionFeedOperationSchema,
  PortfolioExecutionFeedServerMessageSchema,
  PortfolioExecutionFeedSnapshotSchema,
  PortfolioFeedLiveDescriptorSchema,
  UserExecutionFeedClientMessageSchema,
  UserExecutionFeedOperationSchema,
  UserExecutionFeedServerMessageSchema,
  UserExecutionFeedSnapshotSchema,
  UserFeedLiveDescriptorSchema,
  OndoDirectRebalancePlanSchema,
};

const DEFAULT_REQUEST_TIMEOUT_MS = 10_000;
export const EXECUTION_ENGINE_SUPPORTED_CHAIN_NAMESPACES = ["eip155"] as const;
export type ExecutionEngineSupportedChainNamespace =
  (typeof EXECUTION_ENGINE_SUPPORTED_CHAIN_NAMESPACES)[number];

export const isExecutionEngineSupportedChainNamespace = (
  namespace: string,
): namespace is ExecutionEngineSupportedChainNamespace =>
  namespace === "eip155";

export type ExecutionEngineLiveJsonValue = ExecutionLiveJsonValue;

const ExecutionCorrelationIdSchema =
  SharedRequestContracts.ExecutionCorrelationIdSchema;

const executionOperationCreateSchema =
  SharedRequestContracts.executionOperationCreateSchema;
const executionMarketOrderCreateSchema =
  SharedRequestContracts.executionMarketOrderCreateSchema;
const executionMarketOrderQuotePreviewSchema =
  SharedRequestContracts.executionMarketOrderQuotePreviewSchema;
export const executionSpecDrivenBuyPreviewSchema =
  SharedRequestContracts.specDrivenBuyPreviewSchema;
const executionOndoDirectBatchRebalanceCreateSchema =
  SharedRequestContracts.executionOndoDirectBatchRebalanceCreateSchema.superRefine(
    (input, ctx) => {
      // Preserve the BASE client's cross-leg admission rules without imposing
      // them on the shared Worker contract. The leg-vs-cash assetId rule lives
      // in the shared schema (both sides reject), so only uniqueness and the
      // token-address/cash overlap remain client-side.
      const cashTokenKey = input.cashTokenAddress.toLowerCase();
      const seenAssetIds = new Set<string>();
      const seenTokenAddresses = new Set<string>();
      const legs = [
        ...(input.sells ?? []).map((leg, index) => ({
          leg,
          side: "sells" as const,
          index,
        })),
        ...(input.buys ?? []).map((leg, index) => ({
          leg,
          side: "buys" as const,
          index,
        })),
      ];

      for (const { leg, side, index } of legs) {
        const assetKey = leg.assetId.toLowerCase();
        if (seenAssetIds.has(assetKey)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "duplicate assetId across Ondo Direct batch legs",
            path: [side, index, "assetId"],
          });
        }
        seenAssetIds.add(assetKey);

        if (leg.assetTokenAddress !== undefined) {
          const tokenKey = leg.assetTokenAddress.toLowerCase();
          if (tokenKey === cashTokenKey) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "Ondo Direct batch leg assetTokenAddress must differ from cashTokenAddress",
              path: [side, index, "assetTokenAddress"],
            });
          }
          if (seenTokenAddresses.has(tokenKey)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message:
                "duplicate assetTokenAddress across Ondo Direct batch legs",
              path: [side, index, "assetTokenAddress"],
            });
          }
          seenTokenAddresses.add(tokenKey);
        }
      }
    },
  );
const OndoDirectRebalanceTargetSchema =
  SharedRequestContracts.OndoDirectRebalanceTargetSchema;
const executionOndoDirectRebalancePlanCreateSchema =
  SharedRequestContracts.executionOndoDirectRebalancePlanCreateSchema;
const executionOndoDirectRebalancePlanPreviewRequestSchema =
  SharedRequestContracts.executionOndoDirectRebalancePlanPreviewSchema;
const executionWithdrawalCreateSchema =
  SharedRequestContracts.executionWithdrawalCreateSchema;
const executionTransferCreateSchema =
  SharedRequestContracts.executionTransferCreateSchema;
const executionBridgeCreateSchema =
  SharedRequestContracts.executionBridgeCreateSchema;
const executionDepositIntentCreateSchema =
  SharedRequestContracts.executionDepositIntentCreateSchema;
const executionDepositTransactionAttachSchema =
  SharedRequestContracts.executionDepositTransactionAttachSchema;
const DepositTransferReferenceSchema =
  SharedRequestContracts.DepositTransferReferenceSchema;
const executionOperationEventSchema =
  SharedRequestContracts.executionOperationEventSchema;
const executionOperationSettlementAckSchema =
  SharedRequestContracts.executionOperationSettlementAckSchema;
const rebalanceCreateSchema = SharedRequestContracts.rebalanceCreateSchema;

export const executionSpecDrivenBuyCreateSchema =
  SharedRequestContracts.specDrivenBuyCreateSchema;

const executionLiveServerMessageKinds: ReadonlySet<string> = new Set(
  ExecutionLiveServerMessageSchema.options.map(
    (option) => option.shape.kind.value,
  ),
);
const portfolioExecutionFeedServerMessageKinds: ReadonlySet<string> = new Set(
  PortfolioExecutionFeedServerMessageSchema.options.map(
    (option) => option.shape.kind.value,
  ),
);
const userExecutionFeedServerMessageKinds: ReadonlySet<string> = new Set(
  UserExecutionFeedServerMessageSchema.options.map(
    (option) => option.shape.kind.value,
  ),
);

export type ExecutionEngineCreateInput = z.input<
  typeof executionOperationCreateSchema
>;
export type ExecutionEngineRebalanceInput = z.input<
  typeof rebalanceCreateSchema
>;
export type ExecutionEngineRebalanceOperation = z.infer<
  typeof ExecutionRebalanceCreateResponseSchema
>;
export type ExecutionEngineMarketOrderInput = z.input<
  typeof executionMarketOrderCreateSchema
>;
export type ExecutionEngineMarketOrderQuotePreviewInput = z.input<
  typeof executionMarketOrderQuotePreviewSchema
>;
export type ExecutionEngineMarketOrderQuotePreview = z.infer<
  typeof ExecutionMarketOrderQuotePreviewSchema
>;
export type ExecutionEngineSpecDrivenBuyPreviewInput = z.input<
  typeof executionSpecDrivenBuyPreviewSchema
>;
export type ExecutionEngineSpecDrivenBuyPreview = z.infer<
  typeof ExecutionSpecDrivenBuyPreviewResponseSchema
>;
// The plan basis is supplied back to create calls. Expose the schema input so
// callers can use validated CAIP strings without manufacturing branded values.
export type ExecutionEngineSpecDrivenBuyPlanBasis = z.input<
  typeof ExecutionSpecDrivenBuyPlanBasisSchema
>;
export type ExecutionEngineSpecDrivenBuyInput = z.input<
  typeof executionSpecDrivenBuyCreateSchema
>;
export type ExecutionEngineSpecDrivenBuyFunding = z.infer<
  typeof ExecutionSpecDrivenBuyCreatedFundingSchema
>;
export type ExecutionEngineSpecDrivenBuyFundingRecovery = z.infer<
  typeof ExecutionSpecDrivenBuyFundingRecoveryResponseSchema
>;
export type ExecutionEngineSpecDrivenBuyOperation = z.infer<
  typeof ExecutionSpecDrivenBuyOperationResponseSchema
>;
export type ExecutionEngineOndoDirectBatchRebalanceInput = z.input<
  typeof executionOndoDirectBatchRebalanceCreateSchema
>;
export type ExecutionEngineOndoDirectRebalanceTarget = z.infer<
  typeof OndoDirectRebalanceTargetSchema
>;
export type ExecutionEngineOndoDirectRebalancePlanInput = z.input<
  typeof executionOndoDirectRebalancePlanCreateSchema
>;
export type ExecutionEngineOndoDirectRebalancePlanPreviewInput = z.input<
  typeof executionOndoDirectRebalancePlanPreviewRequestSchema
>;
export type ExecutionEngineOndoDirectRebalancePlan = z.infer<
  typeof OndoDirectRebalancePlanSchema
>;
export type ExecutionEngineOndoDirectRebalancePlanPreview = z.infer<
  typeof ExecutionOndoDirectRebalancePlanPreviewSchema
>;
export type ExecutionEngineWithdrawalInput = z.input<
  typeof executionWithdrawalCreateSchema
>;
export type ExecutionEngineTransferInput = z.input<
  typeof executionTransferCreateSchema
>;
export type ExecutionEngineBridgeInput = z.input<
  typeof executionBridgeCreateSchema
>;
export type ExecutionEngineDepositIntentInput = z.input<
  typeof executionDepositIntentCreateSchema
>;
export type ExecutionEngineDepositAttachInput = z.input<
  typeof executionDepositTransactionAttachSchema
>;
export type ExecutionEngineDepositTransferReference = z.infer<
  typeof DepositTransferReferenceSchema
>;
export type ExecutionEngineEventInput = z.input<
  typeof executionOperationEventSchema
>;
export type ExecutionEngineSettlementAckInput = z.input<
  typeof executionOperationSettlementAckSchema
>;

const normalizeOndoDirectZeroMinimums = (
  input: ExecutionEngineOndoDirectBatchRebalanceInput,
): ExecutionEngineOndoDirectBatchRebalanceInput => ({
  ...input,
  ...(input.sells
    ? {
        sells: input.sells.map((leg) => ({
          ...leg,
          minCashOutRaw:
            leg.minCashOutRaw === "0" ? undefined : leg.minCashOutRaw,
        })),
      }
    : {}),
  ...(input.buys
    ? {
        buys: input.buys.map((leg) => ({
          ...leg,
          minTokenOutRaw:
            leg.minTokenOutRaw === "0" ? undefined : leg.minTokenOutRaw,
        })),
      }
    : {}),
});
export type ExecutionEngineOperation = z.infer<
  typeof ExecutionOperationResponseSchema
>;
export type ExecutionEngineLiveDescriptor = z.infer<
  typeof LiveDescriptorSchema
>;
export type ExecutionEngineUserFeedLiveDescriptor = z.infer<
  typeof UserFeedLiveDescriptorSchema
>;
export type ExecutionEngineEventAck = z.infer<
  typeof ExecutionOperationEventAckSchema
>;
export type ExecutionLiveSnapshot = z.infer<typeof ExecutionLiveSnapshotSchema>;
export type ExecutionLiveEvent = z.infer<typeof ExecutionLiveEventSchema>;
export type ExecutionLiveClientMessage = z.infer<
  typeof ExecutionLiveClientMessageSchema
>;
export type ExecutionLiveServerMessage = z.infer<
  typeof ExecutionLiveServerMessageSchema
>;
export type PortfolioExecutionFeedSnapshot = z.infer<
  typeof PortfolioExecutionFeedSnapshotSchema
>;
export type PortfolioExecutionFeedOperation = z.infer<
  typeof PortfolioExecutionFeedOperationSchema
>;
export type PortfolioExecutionFeedClientMessage = z.infer<
  typeof PortfolioExecutionFeedClientMessageSchema
>;
export type PortfolioExecutionFeedServerMessage = z.infer<
  typeof PortfolioExecutionFeedServerMessageSchema
>;
export type UserExecutionFeedSnapshot = z.infer<
  typeof UserExecutionFeedSnapshotSchema
>;
export type UserExecutionFeedOperation = z.infer<
  typeof UserExecutionFeedOperationSchema
>;
export type UserExecutionFeedClientMessage = z.infer<
  typeof UserExecutionFeedClientMessageSchema
>;
export type UserExecutionFeedServerMessage = z.infer<
  typeof UserExecutionFeedServerMessageSchema
>;

export class ExecutionEngineClientError extends Error {
  constructor(
    message: string,
    readonly code:
      | "EXECUTION_ENGINE_NOT_CONFIGURED"
      | "EXECUTION_ENGINE_REQUEST_FAILED"
      | "EXECUTION_ENGINE_NOT_FOUND"
      | "EXECUTION_ENGINE_INVALID_RESPONSE",
    readonly status?: number,
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ExecutionEngineClientError";
  }
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;
export type ExecutionEngineRequestOptions = {
  readonly capability?: {
    readonly portfolioIds?: readonly string[];
    readonly scopes?: readonly CapabilityScope[];
  };
  /**
   * Optional correlation for requests without a correlation-bearing body
   * (for example GETs and event attachments). Create requests derive this
   * automatically from their validated `correlationId` input.
   */
  readonly telemetry?: {
    readonly clientInteractionId?: string;
    readonly correlationId?: string;
  };
};

export type ExecutionEngineRequestContext = {
  readonly body?: string;
  readonly method: "GET" | "POST";
  readonly options?: ExecutionEngineRequestOptions;
  readonly path: string;
};

type HeaderProvider = (
  context?: ExecutionEngineRequestContext,
) => Promise<Record<string, string>> | Record<string, string>;
type CapabilityUnauthorizedHandler = (
  context?: ExecutionEngineRequestContext,
) => Promise<unknown> | unknown;
type WebSocketLike = Pick<
  WebSocket,
  "addEventListener" | "close" | "readyState" | "removeEventListener" | "send"
>;
type WebSocketConstructorLike = new (
  url: string | URL,
  protocols?: string | string[],
) => WebSocketLike;

const normalizeOptionalString = (
  value: string | null | undefined,
): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeBaseUrl = (value: string): string => value.replace(/\/+$/u, "");

export const buildExecutionLiveWebSocketUrl = (params: {
  readonly baseUrl: string;
  readonly operationId: string;
}): string => {
  const url = new URL(
    `/execution/operations/${encodeURIComponent(params.operationId)}/live`,
    normalizeBaseUrl(params.baseUrl),
  );
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
};

export const buildPortfolioExecutionFeedWebSocketUrl = (params: {
  readonly baseUrl: string;
  readonly portfolioId: string;
}): string => {
  const url = new URL(
    `/execution/portfolios/${encodeURIComponent(params.portfolioId)}/live`,
    normalizeBaseUrl(params.baseUrl),
  );
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
};

export const buildUserExecutionFeedWebSocketUrl = (params: {
  readonly baseUrl: string;
  readonly userId: string;
}): string => {
  const url = new URL(
    `/execution/users/${encodeURIComponent(params.userId)}/activity/live`,
    normalizeBaseUrl(params.baseUrl),
  );
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
};

const EXECUTION_FEED_SUBPROTOCOL = "glider.feed.v1";
const EXECUTION_FEED_TOKEN_SUBPROTOCOL_PREFIX = "token.";

const buildExecutionFeedSubprotocols = (
  token: string | null | undefined,
): string[] => [
  EXECUTION_FEED_SUBPROTOCOL,
  ...(token ? [`${EXECUTION_FEED_TOKEN_SUBPROTOCOL_PREFIX}${token}`] : []),
];

const readErrorPayload = async (
  response: Response,
): Promise<{ message: string; details?: Record<string, unknown> }> => {
  const text = await response.text();
  try {
    const payload = JSON.parse(text) as unknown;
    const details =
      typeof payload === "object" && payload !== null
        ? (payload as Record<string, unknown>)
        : undefined;
    const message =
      typeof details?.message === "string" && details.message.trim().length > 0
        ? details.message.trim()
        : typeof details?.error === "string" && details.error.trim().length > 0
          ? details.error.trim()
          : `status ${response.status}`;

    return {
      message,
      ...(details ? { details } : {}),
    };
  } catch {
    const trimmed = text.trim();
    // Short plain-text bodies are useful diagnostics; HTML error pages and
    // other long payloads are not API messages — surface the status instead
    // of dumping markup on callers.
    const isUsableText =
      trimmed.length > 0 && trimmed.length <= 300 && !trimmed.startsWith("<");
    return {
      message: isUsableText ? trimmed : `status ${response.status}`,
    };
  }
};

const withTimeout = async <T>(
  timeoutMs: number,
  run: (signal: AbortSignal) => Promise<T>,
): Promise<T> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await run(controller.signal);
  } finally {
    clearTimeout(timeout);
  }
};

/**
 * Static keepalive control frames. These MUST byte-match
 * `REALTIME_FEED_KEEPALIVE_REQUEST` / `REALTIME_FEED_KEEPALIVE_RESPONSE` in
 * `@repo/realtime-feed/protocol` so the Durable Object's
 * `setWebSocketAutoResponse` pair matches the request exactly and answers it
 * WITHOUT waking the hibernated DO. Duplicated (not imported) to keep this
 * browser-bundled client free of a `@repo/realtime-feed` dependency; the client
 * WS tests pin the exact bytes.
 */
const REALTIME_FEED_KEEPALIVE_REQUEST = '{"kind":"keepalive"}';
const REALTIME_FEED_KEEPALIVE_ACK_KIND = "keepalive_ack";

/**
 * How often the client sends the static keepalive frame on an otherwise-idle
 * socket, and how long it tolerates silence before declaring the socket dead.
 * CF/NAT reap idle sockets with no notification, so without this a parked
 * operation's feed goes quietly stale. The watchdog closes the socket so the
 * consumer's existing `onClose` reconnect path fires.
 */
const KEEPALIVE_INTERVAL_MS = 20_000;
const STALE_CONNECTION_TIMEOUT_MS = 40_000;

type SubscriptionSocket = Pick<WebSocketLike, "close" | "send">;

type LivenessGuard = {
  /** Start the keepalive interval + stale watchdog (call on socket open). */
  readonly start: () => void;
  /** Record an inbound frame of any kind, re-arming the stale watchdog. */
  readonly noteActivity: () => void;
  /** Clear all timers (call on close/unsubscribe). */
  readonly dispose: () => void;
};

const isKeepaliveAck = (value: unknown): boolean =>
  typeof value === "object" &&
  value !== null &&
  (value as { readonly kind?: unknown }).kind ===
    REALTIME_FEED_KEEPALIVE_ACK_KIND;

const hasUnknownFrameKind = (
  value: unknown,
  knownKinds: ReadonlySet<string>,
): boolean => {
  if (
    typeof value !== "object" ||
    value === null ||
    !("kind" in value) ||
    typeof value.kind !== "string"
  ) {
    return false;
  }

  return !knownKinds.has(value.kind);
};

/**
 * Client-side keepalive + stale-connection watchdog, internal to every subscribe
 * helper so all four webapp feed hooks inherit it with no call-site change.
 */
const createLivenessGuard = (socket: SubscriptionSocket): LivenessGuard => {
  let keepaliveTimer: ReturnType<typeof setInterval> | null = null;
  let watchdogTimer: ReturnType<typeof setTimeout> | null = null;

  const clearWatchdog = (): void => {
    if (watchdogTimer !== null) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
  };

  const armWatchdog = (): void => {
    clearWatchdog();
    watchdogTimer = setTimeout(() => {
      // No frame (data, pong, or keepalive_ack) within the stale window: the
      // socket is silently dead. Close it so the consumer's reconnect fires.
      try {
        socket.close();
      } catch {
        // Already closing/closed; nothing to do.
      }
    }, STALE_CONNECTION_TIMEOUT_MS);
  };

  return {
    start: () => {
      armWatchdog();
      keepaliveTimer = setInterval(() => {
        try {
          socket.send(REALTIME_FEED_KEEPALIVE_REQUEST);
        } catch {
          // Socket closing/closed between ticks; the watchdog/close path handles it.
        }
      }, KEEPALIVE_INTERVAL_MS);
    },
    noteActivity: () => {
      armWatchdog();
    },
    dispose: () => {
      clearWatchdog();
      if (keepaliveTimer !== null) {
        clearInterval(keepaliveTimer);
        keepaliveTimer = null;
      }
    },
  };
};

export type ExecutionEngineClient = {
  readonly createOperation: (
    input: ExecutionEngineCreateInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly createRebalance?: (
    input: ExecutionEngineRebalanceInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineRebalanceOperation>;
  readonly createMarketOrder: (
    input: ExecutionEngineMarketOrderInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly previewMarketOrderQuote: (
    input: ExecutionEngineMarketOrderQuotePreviewInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineMarketOrderQuotePreview>;
  readonly previewSpecDrivenBuy: (
    input: ExecutionEngineSpecDrivenBuyPreviewInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineSpecDrivenBuyPreview>;
  readonly createSpecDrivenBuy: (
    input: ExecutionEngineSpecDrivenBuyInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineSpecDrivenBuyOperation>;
  readonly getSpecDrivenBuyFunding?: (
    operationId: string,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineSpecDrivenBuyFunding | null>;
  readonly createOndoDirectBatchRebalance: (
    input: ExecutionEngineOndoDirectBatchRebalanceInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly createOndoDirectRebalanceFromTargets: (
    input: ExecutionEngineOndoDirectRebalancePlanInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly previewOndoDirectRebalancePlan: (
    input: ExecutionEngineOndoDirectRebalancePlanPreviewInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOndoDirectRebalancePlanPreview>;
  readonly createWithdrawal: (
    input: ExecutionEngineWithdrawalInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly createTransfer: (
    input: ExecutionEngineTransferInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly createBridge: (
    input: ExecutionEngineBridgeInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly createDepositIntent: (
    input: ExecutionEngineDepositIntentInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly attachDepositTransaction: (
    operationId: string,
    input: ExecutionEngineDepositAttachInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly getOperation: (
    operationId: string,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineOperation>;
  readonly getOperationLive: (
    operationId: string,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineLiveDescriptor>;
  readonly getPortfolioExecutionFeedLive: (
    portfolioId: string,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineLiveDescriptor>;
  readonly getUserExecutionFeedLive: (
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineUserFeedLiveDescriptor>;
  readonly postEvent: (
    operationId: string,
    input: ExecutionEngineEventInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineEventAck>;
  readonly postSettlementAck: (
    operationId: string,
    input: ExecutionEngineSettlementAckInput,
    options?: ExecutionEngineRequestOptions,
  ) => Promise<ExecutionEngineEventAck>;
  readonly subscribeLiveOperation: (
    operationId: string,
    input: ExecutionEngineLiveSubscriptionInput,
  ) => ExecutionEngineLiveSubscription;
  readonly subscribePortfolioExecutionFeed: (
    portfolioId: string,
    input: PortfolioExecutionFeedSubscriptionInput,
  ) => PortfolioExecutionFeedSubscription;
  readonly subscribeUserExecutionFeed: (
    userId: string,
    input: UserExecutionFeedSubscriptionInput,
  ) => UserExecutionFeedSubscription;
};

export type ConfiguredExecutionEngineClient = ExecutionEngineClient & {
  readonly createRebalance: NonNullable<
    ExecutionEngineClient["createRebalance"]
  >;
};

export type ExecutionEngineLiveSubscriptionInput = {
  readonly lastSequence?: number;
  readonly token?: string | null;
  readonly onSnapshot?: (snapshot: ExecutionLiveSnapshot) => void;
  readonly onEvent?: (event: ExecutionLiveEvent) => void;
  readonly onReplayOverflow?: () => void;
  readonly onError?: (error: ExecutionEngineClientError) => void;
  readonly onClose?: (event: CloseEvent) => void;
  readonly WebSocketImpl?: WebSocketConstructorLike;
};

export type ExecutionEngineLiveSubscription = {
  readonly url: string;
  readonly close: () => void;
  readonly send: (message: ExecutionLiveClientMessage) => void;
};

export type PortfolioExecutionFeedSubscriptionInput = {
  readonly lastSequence?: number;
  readonly token?: string | null;
  readonly onSnapshot?: (snapshot: PortfolioExecutionFeedSnapshot) => void;
  readonly onOperation?: (operation: PortfolioExecutionFeedOperation) => void;
  readonly onReplayOverflow?: () => void;
  readonly onError?: (error: ExecutionEngineClientError) => void;
  readonly onClose?: (event: CloseEvent) => void;
  readonly WebSocketImpl?: WebSocketConstructorLike;
};

export type PortfolioExecutionFeedSubscription = {
  readonly url: string;
  readonly close: () => void;
  readonly send: (message: PortfolioExecutionFeedClientMessage) => void;
};

export type UserExecutionFeedSubscriptionInput = {
  readonly lastSequence?: number;
  readonly token?: string | null;
  readonly onSnapshot?: (snapshot: UserExecutionFeedSnapshot) => void;
  readonly onOperation?: (operation: UserExecutionFeedOperation) => void;
  readonly onReplayOverflow?: () => void;
  readonly onError?: (error: ExecutionEngineClientError) => void;
  readonly onClose?: (event: CloseEvent) => void;
  readonly WebSocketImpl?: WebSocketConstructorLike;
};

export type UserExecutionFeedSubscription = {
  readonly url: string;
  readonly close: () => void;
  readonly send: (message: UserExecutionFeedClientMessage) => void;
};

export const createExecutionEngineClient = (input: {
  readonly baseUrl: string | null | undefined;
  readonly headers?: HeaderProvider;
  readonly onCapabilityUnauthorized?: CapabilityUnauthorizedHandler;
  readonly requestTimeoutMs?: number | null | undefined;
  readonly fetchImpl?: FetchLike;
}): ConfiguredExecutionEngineClient | null => {
  const baseUrl = normalizeOptionalString(input.baseUrl);
  if (!baseUrl) {
    return null;
  }

  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  const fetchImpl = input.fetchImpl ?? fetch;
  const requestTimeoutMs =
    typeof input.requestTimeoutMs === "number" && input.requestTimeoutMs > 0
      ? input.requestTimeoutMs
      : DEFAULT_REQUEST_TIMEOUT_MS;

  const resolveHeaders = async (
    context: ExecutionEngineRequestContext,
  ): Promise<Record<string, string> | undefined> => {
    const authHeaders = (await input.headers?.(context)) ?? {};
    const bodyCorrelationId = (() => {
      if (!context.body) {
        return null;
      }
      try {
        const raw: unknown = JSON.parse(context.body);
        const parsed = z
          .object({
            correlationId: ExecutionCorrelationIdSchema.optional(),
          })
          .passthrough()
          .safeParse(raw);
        return parsed.success ? (parsed.data.correlationId ?? null) : null;
      } catch {
        return null;
      }
    })();
    const optionCorrelationId = ExecutionCorrelationIdSchema.safeParse(
      context.options?.telemetry?.correlationId,
    );
    const correlationId =
      bodyCorrelationId ??
      (optionCorrelationId.success ? optionCorrelationId.data : null);
    const clientInteractionId =
      normalizeOptionalString(
        context.options?.telemetry?.clientInteractionId,
      ) ?? correlationId;
    const telemetryHeaders = {
      ...(clientInteractionId
        ? { "x-client-interaction-id": clientInteractionId }
        : {}),
      ...(correlationId ? { "x-correlation-id": correlationId } : {}),
      "x-request-id": `req_${globalThis.crypto.randomUUID()}`,
    };
    if (context.method === "POST") {
      return {
        ...authHeaders,
        "content-type": "application/json",
        ...telemetryHeaders,
      };
    }

    return {
      ...authHeaders,
      ...telemetryHeaders,
    };
  };

  const send = async (params: {
    readonly path: string;
    readonly method: "GET" | "POST";
    readonly body?: string;
    readonly options?: ExecutionEngineRequestOptions;
  }): Promise<Response> =>
    await withTimeout(requestTimeoutMs, async (signal) => {
      const context: ExecutionEngineRequestContext = {
        body: params.body,
        method: params.method,
        options: params.options,
        path: params.path,
      };
      return await fetchImpl(`${normalizedBaseUrl}${params.path}`, {
        method: params.method,
        headers: await resolveHeaders(context),
        body: params.body,
        signal,
      });
    });

  const isCapabilitySpecificUnauthorized = async (
    response: Response,
  ): Promise<boolean> => {
    if (response.status !== 401 || !input.onCapabilityUnauthorized) {
      return false;
    }

    const { details } = await readErrorPayload(response.clone());
    const error = details?.error;
    return typeof error === "string" && error.startsWith("capability_");
  };

  const sendWithCapabilityRetry = async (params: {
    readonly path: string;
    readonly method: "GET" | "POST";
    readonly body?: string;
    readonly options?: ExecutionEngineRequestOptions;
  }): Promise<Response> => {
    const response = await send(params);
    if (!(await isCapabilitySpecificUnauthorized(response))) {
      return response;
    }

    await input.onCapabilityUnauthorized?.({
      body: params.body,
      method: params.method,
      options: params.options,
      path: params.path,
    });
    return await send(params);
  };

  const requireOk = async <TOutput, TInput>(
    response: Response,
    schema: z.ZodType<TOutput, z.ZodTypeDef, TInput>,
  ): Promise<TOutput> => {
    if (!response.ok) {
      const { message, details } = await readErrorPayload(response);
      throw new ExecutionEngineClientError(
        message,
        response.status === 404
          ? "EXECUTION_ENGINE_NOT_FOUND"
          : "EXECUTION_ENGINE_REQUEST_FAILED",
        response.status,
        details,
      );
    }

    const payload = await response.json();
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      throw new ExecutionEngineClientError(
        "execution_engine_invalid_response",
        "EXECUTION_ENGINE_INVALID_RESPONSE",
      );
    }

    return parsed.data;
  };

  return {
    createOperation: async (rawInput, options) => {
      const input = executionOperationCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/operations",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    createRebalance: async (rawInput, options) => {
      const input = rebalanceCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/rebalances",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionRebalanceCreateResponseSchema,
      );
    },
    createMarketOrder: async (rawInput, options) => {
      const input = executionMarketOrderCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/market-orders",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    previewMarketOrderQuote: async (rawInput, options) => {
      const input = executionMarketOrderQuotePreviewSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/market-order-quotes",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionMarketOrderQuotePreviewSchema,
      );
    },
    previewSpecDrivenBuy: async (rawInput, options) => {
      const input = executionSpecDrivenBuyPreviewSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/buys/preview",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionSpecDrivenBuyPreviewResponseSchema,
      );
    },
    createSpecDrivenBuy: async (rawInput, options) => {
      const input = executionSpecDrivenBuyCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/buys",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionSpecDrivenBuyOperationResponseSchema,
      );
    },
    getSpecDrivenBuyFunding: async (operationId, options) =>
      (
        await requireOk(
          await sendWithCapabilityRetry({
            path: `/execution/buys/${encodeURIComponent(operationId)}/funding`,
            method: "GET",
            options,
          }),
          ExecutionSpecDrivenBuyFundingRecoveryResponseSchema,
        )
      ).funding,
    createOndoDirectBatchRebalance: async (rawInput, options) => {
      const input = executionOndoDirectBatchRebalanceCreateSchema.parse(
        normalizeOndoDirectZeroMinimums(rawInput),
      );
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/ondo-direct/rebalances",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    createOndoDirectRebalanceFromTargets: async (rawInput, options) => {
      const input =
        executionOndoDirectRebalancePlanCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/ondo-direct/rebalances",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    previewOndoDirectRebalancePlan: async (rawInput, options) => {
      const input =
        executionOndoDirectRebalancePlanPreviewRequestSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/ondo-direct/rebalances/preview",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOndoDirectRebalancePlanPreviewSchema,
      );
    },
    createWithdrawal: async (rawInput, options) => {
      const input = executionWithdrawalCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/withdrawals",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    createTransfer: async (rawInput, options) => {
      const input = executionTransferCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/transfers",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    createBridge: async (rawInput, options) => {
      const input = executionBridgeCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/bridges",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    createDepositIntent: async (rawInput, options) => {
      const input = executionDepositIntentCreateSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/deposits",
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    attachDepositTransaction: async (operationId, rawInput, options) => {
      const input = executionDepositTransactionAttachSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: `/execution/deposits/${encodeURIComponent(
            operationId,
          )}/transaction`,
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationResponseSchema,
      );
    },
    getOperation: async (operationId, options) =>
      await requireOk(
        await sendWithCapabilityRetry({
          path: `/execution/operations/${encodeURIComponent(operationId)}`,
          method: "GET",
          options,
        }),
        ExecutionOperationResponseSchema,
      ),
    getOperationLive: async (operationId, options) =>
      await requireOk(
        await sendWithCapabilityRetry({
          path: `/execution/operations/${encodeURIComponent(
            operationId,
          )}/live-descriptor`,
          method: "GET",
          options,
        }),
        LiveDescriptorSchema,
      ),
    getPortfolioExecutionFeedLive: async (portfolioId, options) =>
      await requireOk(
        await sendWithCapabilityRetry({
          path: `/execution/portfolios/${encodeURIComponent(
            portfolioId,
          )}/live-descriptor`,
          method: "GET",
          options,
        }),
        LiveDescriptorSchema,
      ),
    getUserExecutionFeedLive: async (options) =>
      await requireOk(
        await sendWithCapabilityRetry({
          path: "/execution/users/me/activity/live-descriptor",
          method: "GET",
          options,
        }),
        UserFeedLiveDescriptorSchema,
      ),
    postEvent: async (operationId, rawInput, options) => {
      const input = executionOperationEventSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: `/execution/operations/${encodeURIComponent(operationId)}/events`,
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationEventAckSchema,
      );
    },
    postSettlementAck: async (operationId, rawInput, options) => {
      const input = executionOperationSettlementAckSchema.parse(rawInput);
      return await requireOk(
        await sendWithCapabilityRetry({
          path: `/execution/operations/${encodeURIComponent(
            operationId,
          )}/settlement`,
          method: "POST",
          body: JSON.stringify(input),
          options,
        }),
        ExecutionOperationEventAckSchema,
      );
    },
    subscribeLiveOperation: (operationId, subscriptionInput) => {
      const WebSocketImpl = subscriptionInput.WebSocketImpl ?? WebSocket;
      const url = buildExecutionLiveWebSocketUrl({
        baseUrl: normalizedBaseUrl,
        operationId,
      });
      const socket = new WebSocketImpl(
        url,
        buildExecutionFeedSubprotocols(subscriptionInput.token),
      );
      const liveness = createLivenessGuard(socket);

      const sendLiveMessage = (message: ExecutionLiveClientMessage): void => {
        socket.send(
          JSON.stringify(ExecutionLiveClientMessageSchema.parse(message)),
        );
      };

      const onOpen = (): void => {
        sendLiveMessage({
          kind: "hello",
          ...(subscriptionInput.lastSequence === undefined
            ? {}
            : { lastSequence: subscriptionInput.lastSequence }),
        });
        liveness.start();
      };
      const onMessage = (event: MessageEvent): void => {
        if (typeof event.data !== "string") {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "execution_live_binary_message_unsupported",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        // Any frame proves the socket is alive; re-arm the stale watchdog first.
        liveness.noteActivity();

        let raw: unknown;
        try {
          raw = JSON.parse(event.data) as unknown;
        } catch {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "execution_live_invalid_json",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        // Keepalive acks are transport control frames outside the feed schema;
        // liveness is already recorded, so consume them silently.
        if (isKeepaliveAck(raw)) {
          return;
        }

        if (hasUnknownFrameKind(raw, executionLiveServerMessageKinds)) {
          return;
        }

        const parsed = ExecutionLiveServerMessageSchema.safeParse(raw);
        if (!parsed.success) {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "execution_live_invalid_message",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        switch (parsed.data.kind) {
          case "snapshot":
            subscriptionInput.onSnapshot?.(parsed.data.payload);
            break;
          case "event":
            // No per-event ack: it woke the hibernated DO N× per broadcast just
            // to hit an ignore branch (M3). The server still accepts acks for
            // forward-compat, but the client no longer sends them.
            subscriptionInput.onEvent?.(parsed.data.payload);
            break;
          case "error":
            if (parsed.data.code === "replay_overflow") {
              subscriptionInput.onReplayOverflow?.();
              break;
            }
            subscriptionInput.onError?.(
              new ExecutionEngineClientError(
                parsed.data.message,
                "EXECUTION_ENGINE_REQUEST_FAILED",
                undefined,
                {
                  code: parsed.data.code,
                },
              ),
            );
            break;
          case "pong":
            break;
        }
      };
      const onCloseInternal = (event: CloseEvent): void => {
        liveness.dispose();
        subscriptionInput.onClose?.(event);
      };

      socket.addEventListener("open", onOpen);
      socket.addEventListener("message", onMessage);
      socket.addEventListener("close", onCloseInternal);

      return {
        url,
        close: () => {
          liveness.dispose();
          socket.removeEventListener("open", onOpen);
          socket.removeEventListener("message", onMessage);
          socket.removeEventListener("close", onCloseInternal);
          socket.close();
        },
        send: sendLiveMessage,
      };
    },
    subscribePortfolioExecutionFeed: (portfolioId, subscriptionInput) => {
      const WebSocketImpl = subscriptionInput.WebSocketImpl ?? WebSocket;
      const url = buildPortfolioExecutionFeedWebSocketUrl({
        baseUrl: normalizedBaseUrl,
        portfolioId,
      });
      const socket = new WebSocketImpl(
        url,
        buildExecutionFeedSubprotocols(subscriptionInput.token),
      );
      const liveness = createLivenessGuard(socket);

      const sendFeedMessage = (
        message: PortfolioExecutionFeedClientMessage,
      ): void => {
        socket.send(
          JSON.stringify(
            PortfolioExecutionFeedClientMessageSchema.parse(message),
          ),
        );
      };

      const onOpen = (): void => {
        sendFeedMessage({
          kind: "hello",
          ...(subscriptionInput.lastSequence === undefined
            ? {}
            : { lastSequence: subscriptionInput.lastSequence }),
        });
        liveness.start();
      };
      const onMessage = (event: MessageEvent): void => {
        if (typeof event.data !== "string") {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "portfolio_execution_feed_binary_message_unsupported",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        liveness.noteActivity();

        let raw: unknown;
        try {
          raw = JSON.parse(event.data) as unknown;
        } catch {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "portfolio_execution_feed_invalid_json",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        if (isKeepaliveAck(raw)) {
          return;
        }

        if (
          hasUnknownFrameKind(raw, portfolioExecutionFeedServerMessageKinds)
        ) {
          return;
        }

        const parsed = PortfolioExecutionFeedServerMessageSchema.safeParse(raw);
        if (!parsed.success) {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "portfolio_execution_feed_invalid_message",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        switch (parsed.data.kind) {
          case "snapshot":
            subscriptionInput.onSnapshot?.(parsed.data.payload);
            break;
          case "operation":
            // M3: no per-operation ack (see subscribeLiveOperation).
            subscriptionInput.onOperation?.(parsed.data.payload);
            break;
          case "error":
            if (parsed.data.code === "replay_overflow") {
              subscriptionInput.onReplayOverflow?.();
              break;
            }
            subscriptionInput.onError?.(
              new ExecutionEngineClientError(
                parsed.data.message,
                "EXECUTION_ENGINE_REQUEST_FAILED",
                undefined,
                {
                  code: parsed.data.code,
                },
              ),
            );
            break;
          case "pong":
            break;
        }
      };
      const onCloseInternal = (event: CloseEvent): void => {
        liveness.dispose();
        subscriptionInput.onClose?.(event);
      };

      socket.addEventListener("open", onOpen);
      socket.addEventListener("message", onMessage);
      socket.addEventListener("close", onCloseInternal);

      return {
        url,
        close: () => {
          liveness.dispose();
          socket.removeEventListener("open", onOpen);
          socket.removeEventListener("message", onMessage);
          socket.removeEventListener("close", onCloseInternal);
          socket.close();
        },
        send: sendFeedMessage,
      };
    },
    subscribeUserExecutionFeed: (userId, subscriptionInput) => {
      const WebSocketImpl = subscriptionInput.WebSocketImpl ?? WebSocket;
      const url = buildUserExecutionFeedWebSocketUrl({
        baseUrl: normalizedBaseUrl,
        userId,
      });
      const socket = new WebSocketImpl(
        url,
        buildExecutionFeedSubprotocols(subscriptionInput.token),
      );
      const liveness = createLivenessGuard(socket);

      const sendFeedMessage = (
        message: UserExecutionFeedClientMessage,
      ): void => {
        socket.send(
          JSON.stringify(UserExecutionFeedClientMessageSchema.parse(message)),
        );
      };

      const onOpen = (): void => {
        sendFeedMessage({
          kind: "hello",
          ...(subscriptionInput.lastSequence === undefined
            ? {}
            : { lastSequence: subscriptionInput.lastSequence }),
        });
        liveness.start();
      };
      const onMessage = (event: MessageEvent): void => {
        if (typeof event.data !== "string") {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "user_execution_feed_binary_message_unsupported",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        liveness.noteActivity();

        let raw: unknown;
        try {
          raw = JSON.parse(event.data) as unknown;
        } catch {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "user_execution_feed_invalid_json",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        if (isKeepaliveAck(raw)) {
          return;
        }

        if (hasUnknownFrameKind(raw, userExecutionFeedServerMessageKinds)) {
          return;
        }

        const parsed = UserExecutionFeedServerMessageSchema.safeParse(raw);
        if (!parsed.success) {
          subscriptionInput.onError?.(
            new ExecutionEngineClientError(
              "user_execution_feed_invalid_message",
              "EXECUTION_ENGINE_INVALID_RESPONSE",
            ),
          );
          return;
        }

        switch (parsed.data.kind) {
          case "snapshot":
            subscriptionInput.onSnapshot?.(parsed.data.payload);
            break;
          case "operation":
            // M3: no per-operation ack (see subscribeLiveOperation).
            subscriptionInput.onOperation?.(parsed.data.payload);
            break;
          case "error":
            if (parsed.data.code === "replay_overflow") {
              subscriptionInput.onReplayOverflow?.();
              break;
            }
            subscriptionInput.onError?.(
              new ExecutionEngineClientError(
                parsed.data.message,
                "EXECUTION_ENGINE_REQUEST_FAILED",
                undefined,
                {
                  code: parsed.data.code,
                },
              ),
            );
            break;
          case "pong":
            break;
        }
      };
      const onCloseInternal = (event: CloseEvent): void => {
        liveness.dispose();
        subscriptionInput.onClose?.(event);
      };

      socket.addEventListener("open", onOpen);
      socket.addEventListener("message", onMessage);
      socket.addEventListener("close", onCloseInternal);

      return {
        url,
        close: () => {
          liveness.dispose();
          socket.removeEventListener("open", onOpen);
          socket.removeEventListener("message", onMessage);
          socket.removeEventListener("close", onCloseInternal);
          socket.close();
        },
        send: sendFeedMessage,
      };
    },
  };
};

export {
  executionBridgeCreateSchema,
  executionDepositIntentCreateSchema,
  executionDepositTransactionAttachSchema,
  executionMarketOrderCreateSchema,
  executionMarketOrderQuotePreviewSchema,
  executionOndoDirectBatchRebalanceCreateSchema,
  executionOndoDirectRebalancePlanCreateSchema,
  executionOndoDirectRebalancePlanPreviewRequestSchema,
  executionOperationCreateSchema,
  executionOperationEventSchema,
  executionOperationSettlementAckSchema,
  executionTransferCreateSchema,
  executionWithdrawalCreateSchema,
  rebalanceCreateSchema,
};
